import { Modal, message } from 'antd';
import React from 'react';
import EnterpriseDetailPanel from '../EnterpriseDetailPanel';
import styles from './index.module.less';

type EnterpriseDetailOptions = {
  /** Optional initial section, e.g. `archives` from a direct link. */
  tab?: string;
};

type EnterpriseDetailContextValue = {
  openEnterpriseDetail: (
    parkEnterpriseUuid: string,
    options?: EnterpriseDetailOptions,
  ) => void;
};

const EnterpriseDetailContext =
  React.createContext<EnterpriseDetailContextValue | null>(null);

const fallbackOpenEnterpriseDetail = (parkEnterpriseUuid: string) => {
  if (!parkEnterpriseUuid) {
    message.warning('该企业缺少标识，无法打开详情');
    return;
  }
  message.warning('企业详情暂不可用，请刷新页面后重试');
};

export function useEnterpriseDetail() {
  return (
    React.useContext(EnterpriseDetailContext) ?? {
      openEnterpriseDetail: fallbackOpenEnterpriseDetail,
    }
  );
}

export function EnterpriseDetailProvider({
  children,
}: React.PropsWithChildren) {
  const [parkEnterpriseUuid, setParkEnterpriseUuid] = React.useState<string>();
  const [initialTab, setInitialTab] = React.useState<string>('overview');
  const [open, setOpen] = React.useState(false);

  const openEnterpriseDetail = React.useCallback(
    (uuid: string, options?: EnterpriseDetailOptions) => {
      const normalized = String(uuid || '').trim();
      if (!normalized) {
        message.warning('该企业缺少标识，无法打开详情');
        return;
      }
      setParkEnterpriseUuid(normalized);
      setInitialTab(options?.tab || 'overview');
      setOpen(true);
    },
    [],
  );

  const close = React.useCallback(() => setOpen(false), []);

  const value = React.useMemo(
    () => ({ openEnterpriseDetail }),
    [openEnterpriseDetail],
  );

  return (
    <EnterpriseDetailContext.Provider value={value}>
      {children}
      <Modal
        open={open}
        onCancel={close}
        title="企业详情"
        footer={null}
        width="min(1320px, calc(100vw - 48px))"
        centered
        destroyOnHidden
        keyboard
        closable={{ 'aria-label': '关闭' }}
        className={styles.modal}
        styles={{
          body: {
            padding: 16,
            height: 'min(760px, calc(100dvh - 160px))',
            overflow: 'auto',
            overscrollBehavior: 'contain',
          },
        }}
      >
        {parkEnterpriseUuid ? (
          <EnterpriseDetailPanel
            key={`${parkEnterpriseUuid}:${initialTab}`}
            parkEnterpriseUuid={parkEnterpriseUuid}
            initialTab={initialTab}
            onRequestClose={close}
          />
        ) : null}
      </Modal>
    </EnterpriseDetailContext.Provider>
  );
}
